import React, { useState } from "react";
import {
  Heart,
  MessageCircle,
  ExternalLink,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { FaFacebook, FaInstagram } from "react-icons/fa";

const Gallery = () => {
  // =========================================================
  // GALLERY IMAGES
  // Put these images inside your public folder
  // =========================================================

  const posts = [
    {
      id: 1,
      image: "/gallery1.jpg",
    },
    {
      id: 2,
      image: "/gallery2.jpg",
    },
    {
      id: 3,
      image: "/gallery3.jpg",
    },
    {
      id: 4,
      image: "/gallery4.jpg",
    },
    {
      id: 5,
      image: "/gallery5.jpg",
    },
    {
      id: 6,
      image: "/gallery6.jpg",
    },
    {
      id: 7,
      image: "/gallery7.jpg",
    },
    {
      id: 8,
      image: "/gallery8.jpg",
    },
    {
      id: 9,
      image: "/food1.jpg",
    },
    {
      id: 10,
      image: "/food5.jpg",
    },
    {
      id: 11,
      image: "/food2.jpg",
    },
    {
      id: 12,
      image: "/food3.jpg",
    },
  ];

  // =========================================================
  // STATES
  // =========================================================

  const [visiblePosts, setVisiblePosts] = useState(6);
  const [likedPosts, setLikedPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);

  // =========================================================
  // LOAD MORE
  // =========================================================

  const handleLoadMore = () => {
    setVisiblePosts((prev) => Math.min(prev + 6, posts.length));
  };

  // =========================================================
  // LIKE POST
  // =========================================================

  const handleLike = (id) => {
    setLikedPosts((prev) =>
      prev.includes(id) ? prev.filter((postId) => postId !== id) : [...prev, id]
    );
  };

  // =========================================================
  // OPEN LIGHTBOX
  // =========================================================

  const openPost = (post) => {
    setSelectedPost(post);
  };

  // =========================================================
  // CLOSE LIGHTBOX
  // =========================================================

  const closePost = () => {
    setSelectedPost(null);
  };

  // =========================================================
  // NEXT IMAGE
  // =========================================================

  const nextPost = () => {
    if (!selectedPost) return;

    const currentIndex = posts.findIndex((post) => post.id === selectedPost.id);

    const nextIndex = (currentIndex + 1) % posts.length;

    setSelectedPost(posts[nextIndex]);
  };

  // =========================================================
  // PREVIOUS IMAGE
  // =========================================================

  const previousPost = () => {
    if (!selectedPost) return;

    const currentIndex = posts.findIndex((post) => post.id === selectedPost.id);

    const previousIndex = (currentIndex - 1 + posts.length) % posts.length;

    setSelectedPost(posts[previousIndex]);
  };

  return (
    <main className="min-h-screen bg-[#f8f7f3] text-[#111111]">
      {/* =====================================================
          HERO / INSTAGRAM HEADER
      ====================================================== */}

      <section className="px-5 pb-12 pt-24 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-6xl">
          {/* Top Heading */}

          <div className="text-center">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.35em] text-[#777]">
              Follow our journey
            </p>

            <h1 className="font-serif text-4xl italic tracking-tight sm:text-5xl md:text-6xl">
              Check us out on our socials!
            </h1>

            <div className="mx-auto mt-6 h-px w-24 bg-black/20" />

            <p className="mx-auto mt-6 max-w-xl text-sm leading-7 text-gray-600 sm:text-base">
              Get a taste of life at Capeesh. Follow us for delicious food,
              riverside moments, special events and everything happening behind
              the scenes.
            </p>
          </div>

          {/* =================================================
              INSTAGRAM PROFILE BAR
          ================================================== */}

          <div className="mt-12 flex flex-col gap-6 border-y border-black/10 py-5 sm:flex-row sm:items-center sm:justify-between">
            {/* Profile */}

            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-black/10">
                <FaInstagram size={23} strokeWidth={1.8} />
              </div>

              <div>
                <p className="text-sm font-bold tracking-wide">@capeeshparis</p>

                <p className="mt-1 text-xs text-gray-500">
                  Capeesh Craft Kitchen & Cellar
                </p>
              </div>
            </div>

            {/* Follow Button */}

            <a
              href="https://www.instagram.com/capeeshcraftkitchen"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center gap-2 bg-black px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-[#222] hover:shadow-xl"
            >
              Follow on Instagram
              <ExternalLink
                size={14}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </div>

          {/* =================================================
              GALLERY GRID
          ================================================== */}

          <div className="mt-8 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3">
            {posts.slice(0, visiblePosts).map((post) => {
              const isLiked = likedPosts.includes(post.id);

              return (
                <div
                  key={post.id}
                  className="group relative aspect-square cursor-pointer overflow-hidden bg-gray-200"
                  onClick={() => openPost(post)}
                >
                  {/* Image */}

                  <img
                    src={post.image}
                    alt={post.caption}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />

                  {/* Dark Hover Overlay */}

                  <div className="absolute inset-0 bg-black/0 transition-all duration-500 group-hover:bg-black/50" />

                  {/* Instagram Icon */}

                  <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 scale-75 items-center justify-center rounded-full bg-white/95 p-4 opacity-0 shadow-xl transition-all duration-500 group-hover:scale-100 group-hover:opacity-100">
                    <FaInstagram size={25} />
                  </div>

                  {/* Bottom Hover Content */}

                  <div className="absolute bottom-0 left-0 right-0 translate-y-full bg-gradient-to-t from-black/80 to-transparent p-4 pt-12 text-white transition-transform duration-500 group-hover:translate-y-0">
                    <p className="text-xs leading-5">{post.caption}</p>

                    <div className="mt-3 flex items-center gap-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLike(post.id);
                        }}
                        className="flex items-center gap-1.5"
                      >
                        <Heart
                          size={18}
                          fill={isLiked ? "currentColor" : "none"}
                          className={isLiked ? "text-red-500" : "text-white"}
                        />

                        <span className="text-xs">
                          {isLiked ? "Liked" : "Like"}
                        </span>
                      </button>

                      <MessageCircle size={18} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* =================================================
              LOAD MORE BUTTON
          ================================================== */}

          {visiblePosts < posts.length && (
            <div className="mt-12 flex justify-center">
              <button
                onClick={handleLoadMore}
                className="group relative overflow-hidden border border-black bg-transparent px-10 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all duration-500 hover:text-white"
              >
                {/* Animated Background */}

                <span className="absolute inset-0 -translate-x-full bg-black transition-transform duration-500 group-hover:translate-x-0" />

                <span className="relative z-10">Load More Posts</span>
              </button>
            </div>
          )}

          {/* All Posts Loaded */}

          {visiblePosts >= posts.length && (
            <div className="mt-12 text-center">
              <p className="text-sm italic text-black">
                You've seen all our latest moments.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* =====================================================
          BOTTOM CTA
      ====================================================== */}

      <section className="relative overflow-hidden bg-black px-6 py-20 text-center text-white">
        <div className="relative z-10 mx-auto max-w-3xl">
          <p className="mb-4 text-xs uppercase tracking-[0.35em] text-white/50">
            Stay connected
          </p>

          <h2 className="font-serif text-4xl italic sm:text-5xl">
            See you at Capeesh
          </h2>

          <p className="mx-auto mt-5 max-w-lg text-sm leading-7 text-white/60">
            Delicious food, beautiful riverside views and unforgettable moments.
            Follow along and never miss what's happening at Capeesh.
          </p>

          <a
            href="https://www.instagram.com/capeeshcraftkitchen"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-3 border border-white/40 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all duration-300 hover:bg-white hover:text-black"
          >
            <FaInstagram size={18} />
            Follow @capeeshparis
          </a>
          <br/>
          <a
            href="https://www.facebook.com/CapeeshKitchenAndCellar"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-3 border border-white/40 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all duration-300 hover:bg-white hover:text-black"
          >
            <FaFacebook size={18} />
            Follow @capeeshparis
          </a>
        </div>
      </section>

      {/* =====================================================
          IMAGE LIGHTBOX
      ====================================================== */}

      {selectedPost && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/95 p-4"
          onClick={closePost}
        >
          {/* Close Button */}

          <button
            onClick={closePost}
            className="absolute right-5 top-5 z-20 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition hover:bg-white hover:text-black"
          >
            <X size={24} />
          </button>

          {/* Previous Button */}

          <button
            onClick={(e) => {
              e.stopPropagation();
              previousPost();
            }}
            className="absolute left-3 z-20 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition hover:bg-white hover:text-black sm:left-8"
          >
            <ChevronLeft size={25} />
          </button>

          {/* Image Container */}

          <div
            className="relative max-h-[90vh] max-w-5xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedPost.image}
              alt={selectedPost.caption}
              className="max-h-[75vh] max-w-full object-contain"
            />

            <div className="bg-white px-5 py-4 text-black">
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm">{selectedPost.caption}</p>
                <FaInstagram size={20} className="shrink-0" />
              </div>
            </div>
          </div>

          {/* Next Button */}

          <button
            onClick={(e) => {
              e.stopPropagation();
              nextPost();
            }}
            className="absolute right-3 z-20 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition hover:bg-white hover:text-black sm:right-8"
          >
            <ChevronRight size={25} />
          </button>
        </div>
      )}
    </main>
  );
};

export default Gallery;
